// src/engines/VoiceLabCards.jsx
// ══════════════════════════════════════════════════════════════════════════════
// 🎤 EL LABORATORIO DE VOZ — las cuatro tarjetas que se puntúan con el oído.
//
//   ReadAloudCard     lee un pasaje en voz alta → velocidad, precisión, claridad
//   SpeakScoreCard    di una frase              → nota y qué palabra falló
//   ShadowingCard     repite siguiendo el ritmo → nota + acompasamiento
//   MinimalPairCard   ¿dijiste sheep o ship?    → acierto o confusión
//
// ── EN QUÉ SE DIFERENCIAN DE LO QUE HABÍA ────────────────────────────────────
// El ejercicio de pronunciación anterior usaba `SpeechRecognition` del navegador
// y comparaba cadenas de texto: correcto o incorrecto, sin más. Estas cuatro
// usan el oído de Deepgram y devuelven un número con la palabra concreta que
// falló, porque «82 %, la que cuesta es think» le sirve al alumno y
// «incorrecto» no.
//
// La voz modelo también cambia: `SpeechSynthesisUtterance` era la voz robótica
// del navegador, la misma que se descartó para Spacy. Aquí se pide el audio de
// Aura al backend, que es la voz que el alumno ya conoce.
//
// ── EL CONTRATO CON EL MOTOR ─────────────────────────────────────────────────
// Todas reciben { exercise, onCorrect, onWrong, onSkip } como el resto de
// tarjetas, y llaman a onCorrect / onWrong cuando el alumno termina. Ninguna
// bloquea: siempre hay salida, aunque falle el micrófono o la red.
// ══════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Volume2, RotateCcw, ArrowRight, Loader } from 'lucide-react';
import useSpeechScore from './useSpeechScore.js';
import './voice-lab.css';

const API = import.meta.env.VITE_API_URL || '';

// La nota a partir de la cual se da el ejercicio por bueno. 70 no es generoso ni
// severo: es donde un profesor diría «se te entiende, sigue».
const APROBADO = 70;

// ── LA VOZ MODELO ─────────────────────────────────────────────────────────────
// Pide el audio de Aura al backend. Si falla —sin red, sin tokens— se cae a la
// voz del navegador antes que dejar al alumno sin modelo que imitar: un
// ejercicio de imitación sin nada que imitar no es un ejercicio.
async function decir(texto, onDuracion) {
    try {
        const r = await fetch(`${API}/api/tutor/speak`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ text: texto }),
        });
        if (!r.ok) throw new Error(String(r.status));
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        return await new Promise((resolve) => {
            audio.onloadedmetadata = () => onDuracion?.(audio.duration || 0);
            audio.onended = () => { URL.revokeObjectURL(url); resolve(true); };
            audio.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
            audio.play().catch(() => resolve(false));
        });
    } catch {
        if (!('speechSynthesis' in window)) return false;
        const u = new SpeechSynthesisUtterance(texto);
        u.lang = 'en-US';
        u.rate = 0.9;
        window.speechSynthesis.speak(u);
        return true;
    }
}

// ── PIEZAS COMPARTIDAS ────────────────────────────────────────────────────────

function BotonGrabar({ estado, onStart, onStop, etiqueta = 'Grabar' }) {
    if (estado === 'puntuando') {
        return (
            <button className="vlab-btn vlab-btn-grabar" disabled>
                <Loader size={18} className="vlab-girando" /> Evaluando…
            </button>
        );
    }
    if (estado === 'grabando') {
        return (
            <button className="vlab-btn vlab-btn-parar" onClick={onStop}>
                <Square size={16} /> Terminé
            </button>
        );
    }
    return (
        <button className="vlab-btn vlab-btn-grabar" onClick={onStart}>
            <Mic size={18} /> {etiqueta}
        </button>
    );
}

function Medidor({ etiqueta, valor, pie }) {
    const nivel = valor >= 85 ? 'alto' : valor >= 60 ? 'medio' : 'bajo';
    return (
        <div className="vlab-medidor">
            <span className="vlab-medidor-etiqueta">{etiqueta}</span>
            <div className="vlab-barra">
                <div className={`vlab-barra-relleno vlab-${nivel}`} style={{ width: `${Math.max(0, Math.min(100, valor))}%` }} />
            </div>
            <span className={`vlab-medidor-valor vlab-t-${nivel}`}>{Math.round(valor)}</span>
            {pie && <span className="vlab-medidor-pie">{pie}</span>}
        </div>
    );
}

// El pasaje con cada palabra marcada según cómo salió. Es la mitad del valor
// del ejercicio: ver DÓNDE fallaste, no sólo cuánto.
function TextoMarcado({ mapa }) {
    if (!mapa?.length) return null;
    return (
        <p className="vlab-texto-marcado">
            {mapa.map((w, i) => (
                <span key={i} className={`vlab-p vlab-p-${w.estado}`} title={
                    w.estado === 'saltada' ? 'No se oyó' : `Claridad ${w.confianza}%`
                }>
                    {/* `visible` es la palabra tal y como está escrita en el
                        ejercicio — con su mayúscula y su interrogación. La otra,
                        `palabra`, viene normalizada porque es la que el servidor
                        usa para comparar y para guardar; enseñarla convertía
                        "Do you like English? Yes, I do." en "do you like english
                        yes i do". El `||` es la red por si llega un resultado
                        viejo de antes del cambio. */}
                    {w.visible || w.palabra}{' '}
                </span>
            ))}
        </p>
    );
}

function Acciones({ onRepetir, onSeguir, textoSeguir = 'Continuar' }) {
    return (
        <div className="vlab-acciones">
            <button className="vlab-btn vlab-btn-suave" onClick={onRepetir}>
                <RotateCcw size={15} /> Otra vez
            </button>
            <button className="vlab-btn vlab-btn-si" onClick={onSeguir}>
                {textoSeguir} <ArrowRight size={15} />
            </button>
        </div>
    );
}

function Aviso({ texto }) {
    if (!texto) return null;
    return <div className="vlab-aviso">{texto}</div>;
}


// ══════════════════════════════════════════════════════════════════════════════
//  1. LECTURA EN VOZ ALTA
// ══════════════════════════════════════════════════════════════════════════════

export function ReadAloudCard({ exercise, onCorrect, onWrong, onSkip, nivel = 'B1' }) {
    const voz = useSpeechScore();
    const [r, setR] = useState(null);

    const empezar = async () => { setR(null); await voz.start({ modo: 'lectura', objetivo: exercise.passage, nivel }); };
    const terminar = async () => { setR(await voz.stop()); };

    useEffect(() => () => voz.cancel(), []);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vlab-card">
            <div className="vlab-cabecera">
                <span className="vlab-tipo">Lectura en voz alta</span>
                {exercise.focus && <span className="vlab-foco">{exercise.focus}</span>}
            </div>

            {!r ? (
                <>
                    <p className="vlab-pasaje">{exercise.passage}</p>
                    {exercise.hard_words?.length > 0 && (
                        <p className="vlab-dificiles">
                            Ojo con: {exercise.hard_words.map((w, i) => <b key={i}>{w}{i < exercise.hard_words.length - 1 ? ' · ' : ''}</b>)}
                        </p>
                    )}
                    <Aviso texto={voz.error} />
                    <div className="vlab-acciones">
                        <BotonGrabar estado={voz.estado} onStart={empezar} onStop={terminar} etiqueta="Leer en voz alta" />
                        {voz.estado === 'listo' && (
                            <button className="vlab-btn vlab-btn-suave" onClick={onSkip}>Saltar</button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="vlab-nota-grande">
                        <span className="vlab-nota-num">{r.global}</span>
                        <span className="vlab-nota-de">/ 100</span>
                    </div>
                    <div className="vlab-medidores">
                        <Medidor etiqueta="Precisión" valor={r.precision} pie={`${r.palabras_dichas}/${r.palabras_totales} palabras`} />
                        <Medidor etiqueta="Claridad" valor={r.claridad} />
                        <Medidor etiqueta="Ritmo" valor={r.ritmo} pie={`${r.wpm} ppm · objetivo ${r.wpm_objetivo[0]}–${r.wpm_objetivo[1]}`} />
                    </div>
                    <TextoMarcado mapa={r.mapa} />
                    {r.flojas?.length > 0 && (
                        <p className="vlab-dificiles">
                            Las que más costaron: {r.flojas.map((f, i) => (
                                <b key={i}>{f.visible || f.palabra}{i < r.flojas.length - 1 ? ' · ' : ''}</b>
                            ))}
                        </p>
                    )}
                    <Acciones onRepetir={empezar} onSeguir={() => (r.global >= APROBADO ? onCorrect() : onWrong())} />
                </>
            )}
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
//  2. PRONUNCIACIÓN PUNTUADA
// ══════════════════════════════════════════════════════════════════════════════

export function SpeakScoreCard({ exercise, onCorrect, onWrong, onSkip }) {
    const voz = useSpeechScore();
    const [r, setR] = useState(null);
    const [sonando, setSonando] = useState(false);

    const escuchar = async () => { setSonando(true); await decir(exercise.target); setSonando(false); };
    const empezar = async () => { setR(null); await voz.start({ modo: 'pronunciacion', objetivo: exercise.target }); };
    const terminar = async () => { setR(await voz.stop()); };

    useEffect(() => () => voz.cancel(), []);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vlab-card">
            <div className="vlab-cabecera">
                <span className="vlab-tipo">Dilo en voz alta</span>
                {exercise.phonetic && <span className="vlab-fonetica">{exercise.phonetic}</span>}
            </div>

            <p className="vlab-objetivo">{exercise.target}</p>
            {exercise.tip && <p className="vlab-tip">{exercise.tip}</p>}

            {!r ? (
                <>
                    <Aviso texto={voz.error} />
                    <div className="vlab-acciones">
                        <button className="vlab-btn vlab-btn-suave" onClick={escuchar} disabled={sonando}>
                            <Volume2 size={16} /> {sonando ? 'Sonando…' : 'Escuchar'}
                        </button>
                        <BotonGrabar estado={voz.estado} onStart={empezar} onStop={terminar} etiqueta="Decirlo" />
                        {voz.estado === 'listo' && (
                            <button className="vlab-btn vlab-btn-suave" onClick={onSkip}>Saltar</button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="vlab-nota-grande">
                        <span className="vlab-nota-num">{r.nota}</span>
                        <span className="vlab-nota-de">/ 100</span>
                    </div>
                    <p className="vlab-veredicto">{r.mensaje}</p>
                    <TextoMarcado mapa={r.mapa} />
                    <Acciones onRepetir={empezar} onSeguir={() => (r.nota >= APROBADO ? onCorrect() : onWrong())} />
                </>
            )}
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
//  3. SHADOWING
// ══════════════════════════════════════════════════════════════════════════════

export function ShadowingCard({ exercise, onCorrect, onWrong, onSkip }) {
    const voz = useSpeechScore();
    const [r, setR] = useState(null);
    const [sonando, setSonando] = useState(false);
    const [yaEscucho, setYaEscucho] = useState(false);
    const duracionRef = useRef(0);

    const escuchar = async () => {
        setSonando(true);
        await decir(exercise.target, (d) => { duracionRef.current = d; });
        setSonando(false);
        setYaEscucho(true);
    };

    const empezar = async () => {
        setR(null);
        await voz.start({
            modo: 'shadowing',
            objetivo: exercise.target,
            duracion_modelo: duracionRef.current,
        });
    };
    const terminar = async () => { setR(await voz.stop()); };

    useEffect(() => () => voz.cancel(), []);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vlab-card">
            <div className="vlab-cabecera">
                <span className="vlab-tipo">Shadowing — imita el ritmo</span>
            </div>

            <p className="vlab-objetivo">{exercise.target}</p>
            {exercise.rhythm_hint && <p className="vlab-tip">{exercise.rhythm_hint}</p>}

            {!r ? (
                <>
                    <Aviso texto={voz.error} />
                    <div className="vlab-acciones">
                        <button className="vlab-btn vlab-btn-suave" onClick={escuchar} disabled={sonando}>
                            <Volume2 size={16} /> {sonando ? 'Sonando…' : yaEscucho ? 'Escuchar otra vez' : 'Escuchar a Spacy'}
                        </button>
                        {/* No se puede imitar lo que no se ha oído: el botón de
                            grabar no aparece hasta que suena el modelo. */}
                        {yaEscucho && (
                            <BotonGrabar estado={voz.estado} onStart={empezar} onStop={terminar} etiqueta="Repetir igual" />
                        )}
                        {voz.estado === 'listo' && (
                            <button className="vlab-btn vlab-btn-suave" onClick={onSkip}>Saltar</button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="vlab-nota-grande">
                        <span className="vlab-nota-num">{r.global}</span>
                        <span className="vlab-nota-de">/ 100</span>
                    </div>
                    <div className="vlab-medidores">
                        <Medidor etiqueta="Pronunciación" valor={r.nota} />
                        <Medidor etiqueta="Ritmo" valor={r.ritmo} pie={`${r.duracion_alumno}s · Spacy ${r.duracion_modelo}s`} />
                    </div>
                    <p className="vlab-veredicto">
                        {r.veredicto_ritmo === 'acompasado'
                            ? 'Ritmo clavado.'
                            : r.veredicto_ritmo === 'demasiado rapido'
                                ? 'Te adelantaste — Spacy tardó más.'
                                : 'Te quedaste atrás — Spacy fue más rápido.'}
                    </p>
                    <TextoMarcado mapa={r.mapa} />
                    <Acciones onRepetir={empezar} onSeguir={() => (r.global >= APROBADO ? onCorrect() : onWrong())} />
                </>
            )}
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
//  4. PARES MÍNIMOS
// ══════════════════════════════════════════════════════════════════════════════

export function MinimalPairCard({ exercise, onCorrect, onWrong, onSkip }) {
    const voz = useSpeechScore();
    const [r, setR] = useState(null);
    const [sonando, setSonando] = useState(null);

    const escuchar = async (cual) => {
        setSonando(cual);
        await decir(cual === 'target' ? exercise.target : exercise.distractor);
        setSonando(null);
    };

    // ── POR QUE SE DICE LA FRASE Y NO LA PALABRA SUELTA ──────────────────────
    // Medido: `sheep` dicha aislada la reconoce como «she», por muy bien que se
    // pronuncie — una palabra sola no le da al reconocedor ningun contexto del
    // que agarrarse. La misma voz diciendo «The sheep are in the field» acierta
    // al 100 %.
    //
    // Y ademas es mejor asi: las palabras viven en frases, y separar estos dos
    // sonidos dentro de una oracion real es lo que hara falta al hablar.
    const frase = exercise.sentence || exercise.target;

    const empezar = async () => {
        setR(null);
        await voz.start({
            modo: 'par_minimo',
            objetivo: exercise.target,
            distractor: exercise.distractor,
            frase,
        });
    };
    const terminar = async () => { setR(await voz.stop()); };

    useEffect(() => () => voz.cancel(), []);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vlab-card">
            <div className="vlab-cabecera">
                <span className="vlab-tipo">Par mínimo — sepáralos</span>
            </div>

            <div className="vlab-par">
                <button className="vlab-par-lado vlab-par-objetivo" onClick={() => escuchar('target')} disabled={sonando}>
                    <span className="vlab-par-palabra">{exercise.target}</span>
                    <span className="vlab-par-fonetica">{exercise.target_phonetic}</span>
                    <Volume2 size={14} />
                </button>
                <span className="vlab-par-vs">vs</span>
                <button className="vlab-par-lado" onClick={() => escuchar('distractor')} disabled={sonando}>
                    <span className="vlab-par-palabra">{exercise.distractor}</span>
                    <span className="vlab-par-fonetica">{exercise.distractor_phonetic}</span>
                    <Volume2 size={14} />
                </button>
            </div>

            {exercise.tip && <p className="vlab-tip">{exercise.tip}</p>}

            {!r ? (
                <>
                    <p className="vlab-instruccion">
                        Di esta frase, con <b>{exercise.target}</b> y no <b>{exercise.distractor}</b>:
                    </p>
                    <p className="vlab-objetivo">{frase}</p>
                    <Aviso texto={voz.error} />
                    <div className="vlab-acciones">
                        <BotonGrabar estado={voz.estado} onStart={empezar} onStop={terminar} etiqueta="Decir la frase" />
                        {voz.estado === 'listo' && (
                            <button className="vlab-btn vlab-btn-suave" onClick={onSkip}>Saltar</button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className={`vlab-resultado-par ${r.acierto ? 'vlab-acierto' : 'vlab-fallo'}`}>
                        {r.acierto ? '✓' : '✕'} {r.mensaje}
                    </div>
                    <Acciones onRepetir={empezar} onSeguir={() => (r.acierto ? onCorrect() : onWrong())} />
                </>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  5. PRONUNCIACIÓN DE UNA PALABRA — el que ya existía, ahora con el mismo motor
// ══════════════════════════════════════════════════════════════════════════════
//
// ── EL FALLO QUE SUSTITUYE ───────────────────────────────────────────────────
// La versión anterior usaba `SpeechRecognition` del navegador y se colgaba en
// «Escuchando…» para siempre. La causa exacta: a los 6 segundos llamaba a
// `rec.stop()`, pero si el navegador no había reconocido nada eso dispara
// `onend` — y no había manejador de `onend`. La fase se quedaba en "listening"
// sin salida, sin nota y sin forma de avanzar.
//
// Aparte de colgarse, medía con una comparación de cadenas: correcto o
// incorrecto. Ahora usa el mismo oído que los otros cuatro y devuelve un número.
//
// ── UNA PALABRA SUELTA NO SE PUEDE MEDIR ─────────────────────────────────────
// Aquí ponía lo contrario, y la medición que lo respaldaba era demasiado corta:
// tres palabras elegidas a mano. Con diez y audio sintético limpio, CINCO
// fallaban —`questions` 0.62, `through` 0.69, `world` ni se oía— porque son
// 0,1 segundos de audio sin contexto ninguno. Con el micrófono de un portátil
// y un alumno dudando es peor, y cada fallo se va en un reintento que no enseña
// nada y sí abre otra conexión que se paga.
//
// Las mismas diez dentro de una frase natural: UNA fallaba, y nueve salían con
// confianza 1.0.
//
// Así que el alumno lee una frase y la NOTA sale solo de la palabra objetivo
// (`foco`). Sigue siendo un drill de una palabra —que es lo que distingue este
// ejercicio del de leer una frase— y además se practica donde de verdad cuesta:
// encadenada con lo que va antes y después, que es donde un hispanohablante se
// delata.

// La frase entera, con la palabra que se está midiendo marcada. El alumno tiene
// que saber de dónde sale su nota: si lee una frase y se le puntúa una palabra
// sin decírselo, un 40 después de leerla bien parece arbitrario.
function FraseConFoco({ frase, palabra }) {
    if (!frase) return null;
    if (!palabra) return <p className="vlab-objetivo">{frase}</p>;

    // La palabra se limpia ANTES de construir el patrón, dejando sólo letras,
    // dígitos y apóstrofe. Así no hace falta escapar nada: lo que queda no
    // puede contener un carácter con significado en una expresión regular.
    // El texto viene de la IA, y montar un patrón con texto sin filtrar es
    // pedir que un paréntesis suelto rompa la tarjeta entera.
    const limpia = palabra.replace(/[^A-Za-z0-9']/g, '');
    if (!limpia) return <p className="vlab-objetivo">{frase}</p>;

    // `\b` a los lados para que "order" no marque el "order" de "ordered".
    const partes = frase.split(new RegExp('\\b(' + limpia + ')\\b', 'i'));
    return (
        <p className="vlab-objetivo">
            {partes.map((t, i) =>
                t.toLowerCase() === palabra.toLowerCase()
                    ? <span key={i} className="vlab-foco">{t}</span>
                    : <span key={i}>{t}</span>
            )}
        </p>
    );
}

export function PronunciationScoredCard({ exercise, onCorrect, onWrong, onSkip }) {
    const voz = useSpeechScore();
    const [r, setR] = useState(null);
    const [sonando, setSonando] = useState(false);

    const palabra = exercise.word || exercise.target || '';
    // El contenido generado antes de este cambio no trae `sentence`. En vez de
    // dejar esos ejercicios rotos se les pone un marco fijo: es robótico, pero
    // le da al reconocedor el contexto que necesita.
    const frase = exercise.sentence || (palabra ? `Say ${palabra} again.` : '');

    const escuchar = async () => { setSonando(true); await decir(frase); setSonando(false); };
    const empezar = async () => {
        setR(null);
        await voz.start({ modo: 'pronunciacion', objetivo: frase, foco: palabra });
    };
    const terminar = async () => { setR(await voz.stop()); };

    useEffect(() => () => voz.cancel(), []);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vlab-card">
            <div className="vlab-cabecera">
                <span className="vlab-tipo">Pronuncia en voz alta</span>
                {exercise.phonetic && <span className="vlab-fonetica">{exercise.phonetic}</span>}
            </div>

            <FraseConFoco frase={frase} palabra={palabra} />
            {exercise.context && <p className="vlab-tip">{exercise.context}</p>}
            {exercise.tip && <p className="vlab-tip">💡 {exercise.tip}</p>}

            {!r ? (
                <>
                    <Aviso texto={voz.error} />
                    <div className="vlab-acciones">
                        <button className="vlab-btn vlab-btn-suave" onClick={escuchar} disabled={sonando}>
                            <Volume2 size={16} /> {sonando ? 'Sonando…' : 'Escuchar modelo'}
                        </button>
                        <BotonGrabar estado={voz.estado} onStart={empezar} onStop={terminar} etiqueta="Pronunciar" />
                        {/* Saltar SIEMPRE disponible, incluso grabando: el fallo
                            anterior dejaba al alumno encerrado sin salida. */}
                        <button className="vlab-btn vlab-btn-suave" onClick={() => { voz.cancel(); onSkip?.(); }}>
                            Saltar
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="vlab-nota-grande">
                        <span className="vlab-nota-num">{r.nota}</span>
                        <span className="vlab-nota-de">/ 100</span>
                    </div>
                    <p className="vlab-veredicto">{r.mensaje}</p>
                    <Acciones onRepetir={empezar} onSeguir={() => (r.nota >= APROBADO ? onCorrect() : onWrong())} />
                </>
            )}
        </div>
    );
}
